import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DropdownBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipExplicitCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipExplicitCondition';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {customFieldTypes} from '../../../../helpers/customFieldHelpers';
import {computeCustomFieldIconName, renderCustomFieldType} from '../../../../helpers/customFieldViewHelpers';

/*
 * Constants.
 */

const dropdownWidth = 260; // px.

/*
 * Props.
 */

interface CustomFieldTypesDropdownProps {
  selectedType: CustomFieldType;
  availableTypes?: ReadonlyArray<CustomFieldType>;
  renderDisabledTooltip?: RendererOf<CustomFieldType>;
  renderDropdownItemDisabledTooltip?: Renderer;
  onSelect: (type: CustomFieldType) => void;
}

/*
 * Style.
 */

const StyledDropdownValueDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

/*
 * Component.
 */

export const CustomFieldTypesDropdown: FC<CustomFieldTypesDropdownProps> = (props) => {
  const {selectedType, availableTypes, renderDropdownItemDisabledTooltip, renderDisabledTooltip, onSelect} =
    props;
  const isDisabled = availableTypes?.length === 1;
  return (
    <PopoverCoordinator
      isDisabled={isDisabled}
      renderButton={(hasHover) => (
        <TooltipCoordinator
          render={() => renderDisabledTooltip && renderDisabledTooltip(selectedType)}
          placement="top"
          customMargin={-16}
        >
          <DropdownBox
            isRequired
            hasHover={hasHover}
            isDisabled={isDisabled}
            label={<Trans id="custom-field-type-dropdown-title">Type</Trans>}
            value={renderDropdownValue(selectedType)}
            boxStyles={{marginBottom: 0}}
          />
        </TooltipCoordinator>
      )}
      renderModal={(popoverProps) => (
        <Dropdown {...popoverProps} width={dropdownWidth}>
          <DropdownList>
            {renderDropdownItems(
              selectedType,
              availableTypes || customFieldTypes,
              (type) => {
                onSelect(type);
                popoverProps.onRequestClose();
              },
              renderDropdownItemDisabledTooltip,
            )}
          </DropdownList>
        </Dropdown>
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownValue(type: CustomFieldType) {
  return (
    <StyledDropdownValueDiv>
      <Icon name={computeCustomFieldIconName(type, VisualSizesEnum.SMALL)} />
      {renderCustomFieldType(type)}
    </StyledDropdownValueDiv>
  );
}

function renderDropdownItems(
  selectedType: CustomFieldType,
  availableTypes: ReadonlyArray<CustomFieldType>,
  onSelect: (type: CustomFieldType) => void,
  renderDropdownItemDisabledTooltip?: Renderer,
) {
  return customFieldTypes.map((customFieldType) => {
    const isSelected = selectedType === customFieldType;
    const isDisabled = !availableTypes.includes(customFieldType);
    return (
      <TooltipCoordinator
        key={customFieldType}
        render={() => renderDropdownItemDisabledTooltip && renderDropdownItemDisabledTooltip()}
        placement="top"
      >
        <TooltipExplicitCondition isEnabled={isDisabled} />
        <DropdownItem
          key={customFieldType}
          isChecked={isSelected}
          isDisabled={isDisabled}
          onSelect={() => onSelect(customFieldType)}
        >
          <DropdownItemIcon
            iconName={computeCustomFieldIconName(customFieldType, VisualSizesEnum.SMALL)}
            label={null}
            iconSize={16}
          />
          <DropdownItemContent isBold={isSelected}>
            {renderCustomFieldType(customFieldType)}
          </DropdownItemContent>
        </DropdownItem>
      </TooltipCoordinator>
    );
  });
}
